import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, MONTHS } from "../api";
import type {
  ChildRule,
  DraftChoice,
  Me,
  OrderChild,
  OrderDay,
  OrderPayload,
  Submission,
} from "../types";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import {
  DOW_LONG,
  isoDate,
  mondayOfWeek,
  todayIso,
  weekdayDates,
} from "../lib/calendar";
import { Badge, Button, Card, PageHeader, Pager, SkeletonRows, usePaged } from "../components/ui";

interface Outcome {
  status: string;
  detail: string;
}
const STATUS_TONE: Record<string, string> = {
  synced: "synced",
  pending: "pending",
  unmatched: "unmatched",
  error: "error",
};

const CHILD_COLOR = ["#0e7c86", "#9a6b12", "#2f8a4e", "#7b4fa8", "#c2452f"];

const dishFor = (day: OrderDay, c: DraftChoice) =>
  c === "Hot" ? day.dishes.hot : c === "Swap" ? day.dishes.swap : c === "Veggie" ? day.dishes.veggie : null;

function childState(child: OrderChild, buyableDates: string[]) {
  const picks = child.submitted ? child.submitted.picks : child.picks;
  const filled = buyableDates.filter((d) => picks[d]).length;
  if (child.submitted?.mismatch.length)
    return { label: "ordered, check picks", tone: "warn", filled, total: buyableDates.length };
  if (child.submitted)
    return { label: "ordered", tone: "synced", filled, total: buyableDates.length };
  if (filled === 0)
    return { label: "not started", tone: "error", filled, total: buyableDates.length };
  if (filled >= buyableDates.length)
    return { label: "filled, not submitted", tone: "pending", filled, total: buyableDates.length };
  return {
    label: `${buyableDates.length - filled} days left`,
    tone: "warn",
    filled,
    total: buyableDates.length,
  };
}

const rangeLabel = (dates: string[]) => {
  if (dates.length === 0) return "";
  const a = dates[0];
  const b = dates[dates.length - 1];
  const fmt = (d: string) => `${MONTHS[+d.slice(5, 7) - 1].slice(0, 3)} ${+d.slice(8)}`;
  const end = a.slice(0, 7) === b.slice(0, 7) ? String(+b.slice(8)) : fmt(b);
  return `${fmt(a)} – ${end}`;
};

function WeekOverview() {
  const thisMonday = isoDate(mondayOfWeek(new Date(`${todayIso()}T00:00:00Z`)));
  const [anchor, setAnchor] = useState(thisMonday);
  const shiftWeek = (delta: number) => {
    const d = new Date(`${anchor}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setAnchor(isoDate(d));
  };

  const today = todayIso();
  const weekDates = weekdayDates(new Date(`${anchor}T00:00:00Z`));
  // On the current or a future week, drop days that have already passed.
  const visibleDates =
    anchor < thisMonday ? weekDates : weekDates.filter((d) => d >= today);

  // The week can straddle a month boundary; fetch each month it touches.
  const months = [
    ...new Set(weekDates.map((d) => d.slice(0, 7))),
  ].map((k) => ({ year: +k.slice(0, 4), month: +k.slice(5, 7) }));
  const qA = useQuery({
    queryKey: ["order-draft", months[0].year, months[0].month],
    queryFn: () =>
      api.get<OrderPayload>(
        `/order/draft?year=${months[0].year}&month=${months[0].month}`,
      ),
  });
  const qB = useQuery({
    queryKey: months[1]
      ? ["order-draft", months[1].year, months[1].month]
      : ["order-draft", "none"],
    queryFn: () =>
      api.get<OrderPayload>(
        `/order/draft?year=${months[1]!.year}&month=${months[1]!.month}`,
      ),
    enabled: !!months[1],
  });

  const payloadByMonth = new Map<string, OrderPayload>();
  for (const p of [qA.data, qB.data]) {
    if (p) payloadByMonth.set(`${p.year}-${String(p.month).padStart(2, "0")}`, p);
  }
  const anchorPayload = payloadByMonth.get(anchor.slice(0, 7)) ?? qA.data;
  const children = anchorPayload?.children ?? [];
  const buyableDates = (anchorPayload?.days ?? [])
    .filter((d) => !d.noSchool && d.choices.length > 0)
    .map((d) => d.date);

  const dayFor = (date: string): OrderDay | undefined =>
    payloadByMonth.get(date.slice(0, 7))?.days.find((d) => d.date === date);
  const pickFor = (childRuleId: number, date: string) => {
    const c = payloadByMonth
      .get(date.slice(0, 7))
      ?.children.find((x) => x.childRuleId === childRuleId);
    if (!c) return null;
    return (c.submitted ? c.submitted.picks : c.picks)[date] ?? null;
  };

  const loading = qA.isLoading || (!!months[1] && qB.isLoading);

  return (
    <Card
      title="This week"
      sub={rangeLabel(weekDates)}
      actions={
        <div className="cluster">
          <Button size="sm" variant="ghost" icon="arrow-left" onClick={() => shiftWeek(-1)}>
            <span className="hide-sm">Prev</span>
          </Button>
          {anchor !== thisMonday && (
            <Button size="sm" variant="ghost" onClick={() => setAnchor(thisMonday)}>
              Today
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => shiftWeek(1)}>
            <span className="hide-sm">Next</span> <Icon name="arrow-right" size={15} />
          </Button>
          <Link className="btn sm" to="/order">
            Order
          </Link>
        </div>
      }
    >
      {loading ? (
        <SkeletonRows n={3} />
      ) : children.length === 0 ? (
        <p className="muted">No children yet.</p>
      ) : (
        <>
          <div className="dash-kids">
            {children.map((c, i) => {
              const s = childState(c, buyableDates);
              return (
                <div key={c.childRuleId} className="dash-kid">
                  <b
                    className="dash-name"
                    style={{ color: CHILD_COLOR[i % CHILD_COLOR.length] }}
                  >
                    {c.label}
                  </b>
                  <Badge tone={s.tone}>{s.label}</Badge>
                  {!c.submitted && (
                    <span className="faint small">
                      {s.filled}/{s.total} days
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {visibleDates.length === 0 ? (
            <p className="muted small" style={{ marginTop: 8 }}>
              No lunch days left this week. Use Next to look ahead.
            </p>
          ) : (
            <div className="menu-grid week-grid" style={{ marginTop: 6 }}>
              {visibleDates.map((date, di) => {
                const day = dayFor(date);
                const noServe = !day || day.noSchool || day.choices.length === 0;
                return (
                  <div
                    key={date}
                    className={`cell ${date === today ? "today" : ""} ${noServe ? "noserve" : ""}`}
                  >
                    <div className="cell-head">
                      <span className="dow">
                        {DOW_LONG[new Date(`${date}T00:00:00Z`).getUTCDay() - 1] ??
                          DOW_LONG[di]}
                      </span>
                      <span className="dnum">{Number(date.slice(8))}</span>
                    </div>
                    {noServe ? (
                      <div className="noserve-label">
                        {day?.noServeNote || (day ? "No lunch" : "No menu yet")}
                      </div>
                    ) : (
                      <div className="dash-day">
                        {children.map((c, i) => {
                          const pick = pickFor(c.childRuleId, date);
                          const dish = pick ? dishFor(day, pick.choice) : null;
                          return (
                            <div
                              key={c.childRuleId}
                              className={`dash-pick ${pick ? "" : "none"}`}
                            >
                              <b
                                className="dash-name"
                                style={{
                                  color: CHILD_COLOR[i % CHILD_COLOR.length],
                                }}
                              >
                                {c.label}
                              </b>
                              <span className="dash-dish">
                                {!pick
                                  ? "not ordered"
                                  : pick.choice === "No Lunch"
                                    ? "skipping"
                                    : dish || pick.choice}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function Dashboard() {
  const me = useOutletContext<Me>();
  const qc = useQueryClient();
  const toast = useToast();

  const subs = useQuery({
    queryKey: ["submissions"],
    queryFn: () => api.get<{ submissions: Submission[] }>("/submissions"),
  });
  const rules = useQuery({
    queryKey: ["rules"],
    queryFn: () => api.get<{ rules: ChildRule[] }>("/rules"),
  });

  const runIngest = useMutation({
    mutationFn: () => api.post<{ outcomes: Outcome[] }>("/ingest/run"),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["submissions"] });
      qc.invalidateQueries({ queryKey: ["order-draft"] });
      if (d.outcomes.length === 0) toast.show("No new messages in the app inbox.");
      else {
        const synced = d.outcomes.filter((o) => o.status === "synced").length;
        toast.success(
          `Processed ${d.outcomes.length} message${d.outcomes.length === 1 ? "" : "s"}` +
            (synced ? ` · ${synced} synced` : ""),
        );
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const loading = subs.isLoading || rules.isLoading;
  const rows = subs.data?.submissions ?? [];
  const { slice: pagedOrders, pager: ordersPager } = usePaged(rows, 6);
  const hasChildren = (rules.data?.rules?.length ?? 0) > 0;
  const hasOrders = rows.length > 0;
  const canOrder = me.isAdmin || me.orderAssist;

  return (
    <>
      <PageHeader
        title="Dashboard"
        desc="What each child is eating, and their order status."
        action={
          me.isAdmin && (
            <Button
              variant="secondary"
              icon="refresh"
              loading={runIngest.isPending}
              onClick={() => runIngest.mutate()}
            >
              Check inbox
            </Button>
          )
        }
      />

      {loading ? (
        <SkeletonRows n={3} />
      ) : (
        <>
          {!hasChildren && (
            <div className="callout">
              <Icon name="user" size={20} />
              <div className="grow">
                <b>First, add a profile for each child.</b>
                <div className="small">
                  A profile links a name on the order form to a calendar. Nothing
                  gets scheduled until at least one exists.
                </div>
              </div>
              <Link className="btn sm" to="/children">
                <Icon name="plus" size={15} />
                Add a child
              </Link>
            </div>
          )}

          {hasChildren && canOrder && <WeekOverview />}

          {!hasOrders && <GettingStarted me={me} hasChildren={hasChildren} />}

          {hasOrders && (
            <>
              <h2 style={{ margin: "18px 0 12px" }}>Order history</h2>
              <div className="stack">
                {pagedOrders.map((s) => (
                  <Link
                    key={s.id}
                    to={`/submissions/${s.id}`}
                    className="rowcard"
                  >
                    <div className="grow">
                      <div className="title">
                        {s.student_first} {s.student_last}
                        {s.grade && (
                          <span className="muted small"> · grade {s.grade}</span>
                        )}
                      </div>
                      <div className="meta">
                        {MONTHS[s.month - 1]} {s.year} · {s.buyingDays ?? 0} lunch
                        day{(s.buyingDays ?? 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                    <Badge tone={STATUS_TONE[s.status] ?? "info"} dot>
                      {s.status}
                    </Badge>
                    <Icon name="chevron-right" className="faint" />
                  </Link>
                ))}
              </div>
              <Pager {...ordersPager} label="orders" />

              <details className="how-details">
                <summary>How it works</summary>
                <GettingStarted me={me} hasChildren={hasChildren} compact />
              </details>
            </>
          )}
        </>
      )}
    </>
  );
}

function CopyBox({ value }: { value: string }) {
  const toast = useToast();
  return (
    <div className="copybox">
      <code>{value}</code>
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast.success("Copied");
          } catch {
            toast.error("Couldn't copy");
          }
        }}
      >
        <Icon name="copy" size={15} />
        Copy
      </button>
    </div>
  );
}

function GettingStarted({
  me,
  hasChildren,
  compact,
}: {
  me: Me;
  hasChildren: boolean;
  compact?: boolean;
}) {
  return (
    <Card
      title={compact ? undefined : "Getting started"}
      sub={compact ? undefined : "Three steps, then it runs on its own."}
    >
      <ol className="steps">
        <li className={hasChildren ? "done" : "active"}>
          <span className="num">
            {hasChildren ? <Icon name="check" size={15} /> : 1}
          </span>
          <div className="body">
            <b>Add a profile for each child</b>
            <p>
              Their first name (and grade / school), the Google Calendar their
              lunch days should land on, what time the event starts, and how the
              title reads, e.g. <span className="mono">(L) Lunch: Pizza</span>.
            </p>
            {!hasChildren && (
              <Link className="btn sm" to="/children">
                <Icon name="plus" size={15} />
                Add a child
              </Link>
            )}
          </div>
        </li>

        <li className={hasChildren ? "active" : ""}>
          <span className="num">2</span>
          <div className="body">
            <b>Forward your Google&nbsp;Forms order email</b>
            <p>
              After you submit the school&rsquo;s lunch order form, Google emails
              you a receipt with the subject{" "}
              <b>&ldquo;Thanks for filling out this form&hellip;&rdquo;</b>.
              Forward that entire email, one per child, to:
            </p>
            <CopyBox value={me.appInbox} />
            <p className="small muted">
              Forward from{" "}
              {me.senders.length ? (
                <b>{me.senders.join(" or ")}</b>
              ) : (
                <b>{me.user.email}</b>
              )}
              . To let a partner forward from their address too, add it under{" "}
              <Link to="/settings">Settings</Link>.
            </p>
          </div>
        </li>

        <li>
          <span className="num">3</span>
          <div className="body">
            <b>That&rsquo;s it, check your calendar</b>
            <p>
              Within a few minutes the days you picked appear on the calendar,
              titled with the actual menu item. Change your order later and
              forward the new receipt; the events update in place, no duplicates.
            </p>
          </div>
        </li>
      </ol>
    </Card>
  );
}
